import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheckCircle, FiClock } from 'react-icons/fi';
import moment from 'moment';
import { cn } from '@/lib/utils';
import { Sheet, SheetBody, SheetContent } from '@/components/ui/sheet';
import { ExamsResolutionAnswerKey } from '@/pages/exams/exams-resolution/exams-resolution-answer-key';
import simuladoIcon from '@/assets/images/infantil/simulado.png';

const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

// Card lúdico de simulado (aluno + tema infantil). Espelha o PlayfulTrilhaCard /
// HomeNavCard: fundo cream, ilustração 3D, badge de status, botão chanfrado 3D.
// A variante corporativa (admin / tema padrão) continua em mock-exams-card.jsx.
export function PlayfulMockExamsCard({ mockQuestionBook, index = 0 }) {
  const [showModal, setShowModal] = useState(false);

  const status = mockQuestionBook?.status;
  const isAcceptingAnswers = status === 'accepting_answers';
  const isExamDone = status === 'approved' || status === 'disapproved';
  const isInAnalisys = status === 'review';
  const isExpired = moment().isAfter(moment(mockQuestionBook?.['finished-at']));

  const questionCount = mockQuestionBook?.['question-count'] ?? 0;
  const taxonomies = mockQuestionBook?.taxonomies || [];
  const publishedAt = mockQuestionBook?.['published-at'];

  // Estado visual: 'done' e 'analysis' têm precedência sobre o prazo.
  const state = isExamDone ? 'done' : isInAnalisys ? 'analysis' : isExpired ? 'expired' : isAcceptingAnswers ? 'accepting' : 'unavailable';

  const badge = {
    accepting: { text: 'Aberto', className: 'bg-[var(--color-bg-mission-soft)] text-[var(--color-mission-medium)]' },
    done: { text: 'Realizado', className: 'bg-success-light text-success' },
    analysis: { text: 'Em análise', className: 'bg-info-light text-info' },
    expired: { text: 'Prazo encerrado', className: 'bg-neutral-100 text-neutral-400' },
    unavailable: { text: 'Em breve', className: 'bg-neutral-100 text-neutral-400' }
  }[state];

  return (
    <>
      <article
        style={{ animation: `trilha-in 380ms ${EASE_OUT} ${index * 70}ms both` }}
        className={cn(
          'group relative flex flex-col overflow-hidden rounded-[24px] border border-neutral-100 bg-[var(--color-bg-cream)] p-6',
          'shadow-card transition-[box-shadow,transform,border-color] duration-150 ease-out-strong hover:-translate-y-0.5 hover:shadow-card-hover'
        )}
      >
        {/* Faixa de céu suave no topo (teal, combinando com o ícone). */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-16 rounded-t-[24px]"
          style={{ background: 'linear-gradient(180deg, rgba(214,253,255,0.5) 0%, transparent 100%)' }}
        />

        {/* Decoração sutil no canto. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-2 right-3 text-2xl opacity-25"
        >
          ✏️
        </span>

        {/* Topo: ilustração 3D + badge de status. */}
        <div className="relative mb-5 flex items-start justify-between">
          <img
            src={simuladoIcon}
            alt=""
            aria-hidden="true"
            className="h-20 w-20 object-contain drop-shadow-sm"
          />
          <span className={cn('rounded-full px-2.5 py-1 text-xs font-bold', badge.className)}>{badge.text}</span>
        </div>

        <h3
          className="text-lg font-bold"
          style={{ color: '#222731' }}
        >
          {mockQuestionBook?.title}
        </h3>

        {/* Matérias como pílulas. */}
        {taxonomies.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {taxonomies.slice(0, 2).map((tax, i) => (
              <span
                key={i}
                className="rounded-full bg-[var(--color-bg-teal-soft)] px-2.5 py-0.5 text-xs font-semibold text-[var(--color-primary)]"
              >
                {tax?.name || tax}
              </span>
            ))}
            {taxonomies.length > 2 && <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-semibold text-neutral-400">+{taxonomies.length - 2}</span>}
          </div>
        )}

        {/* Meta: nº de questões + data de publicação. */}
        <div className="mb-6 mt-4 flex flex-1 items-center gap-4 text-sm text-neutral-500">
          <span className="flex items-center gap-1.5 font-semibold">
            <FiCheckCircle className="h-4 w-4 text-[var(--color-mission-medium)]" />
            {questionCount} {questionCount === 1 ? 'questão' : 'questões'}
          </span>
          {publishedAt && (
            <span className="flex items-center gap-1.5">
              <FiClock className="h-4 w-4" />
              {moment(publishedAt).format('DD/MM/YYYY')}
            </span>
          )}
        </div>

        {/* CTA chanfrado por estado. */}
        {state === 'accepting' && (
          <Link
            to={`/simulados/sobre/${mockQuestionBook?.id}`}
            className="group/btn relative block w-full overflow-hidden rounded-full py-3 text-center text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
            style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 4px 0 var(--color-mission-dark), 0 6px 12px rgba(245,123,46,0.35)' }}
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%)' }}
            />
            <span className="relative inline-flex items-center justify-center gap-2">
              Fazer simulado
              <FiArrowRight className="h-4 w-4 transition-transform duration-150 group-hover/btn:translate-x-0.5" />
            </span>
          </Link>
        )}

        {state === 'done' && (
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="relative w-full overflow-hidden rounded-full py-3 text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
            style={{ background: 'var(--gradient-btn-start)', boxShadow: '0 4px 0 var(--color-secondary-dark), 0 6px 12px rgba(68,43,140,0.35)' }}
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 100%)' }}
            />
            <span className="relative">Ver gabarito</span>
          </button>
        )}

        {(state === 'analysis' || state === 'expired' || state === 'unavailable') && (
          <button
            type="button"
            disabled
            className="w-full cursor-not-allowed rounded-full border border-neutral-200 bg-neutral-100 py-3 text-sm font-bold text-neutral-400"
          >
            {badge.text}
          </button>
        )}
      </article>

      <Sheet
        open={showModal}
        onOpenChange={setShowModal}
      >
        <SheetContent size="md">
          <SheetBody className="pb-6 pt-12">
            <ExamsResolutionAnswerKey
              examId={mockQuestionBook.id}
              amountQuestions={mockQuestionBook['question-count']}
              title="Gabarito do simulado"
            />
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
}
